import React from "react"

interface DayStats {
  _id: string
  date: string
  stateCounts: any[]
  stateTransitions: any[]
  totalApplications: number
  createdAt: string
  updatedAt: string
}

interface StatsTableRowProps {
  state: string
  statsData: any[]
  viewMode: "daily" | "weekly" | "monthly"
  onTotalClick: (state: string, data: any) => Promise<void>
  onTransitionClick: (type: "incoming" | "outgoing", state: string, data: any) => void
  getStateDisplayName: (state: string) => string
  getStateColor: (state: string) => string
  hasBrandData: (data: any, state: string, type: "stateCounts" | "incoming" | "outgoing") => boolean
}

const StatsTableRow: React.FC<StatsTableRowProps> = ({
  state,
  statsData,
  viewMode,
  onTotalClick,
  onTransitionClick,
  getStateDisplayName,
  getStateColor,
  hasBrandData,
}) => {
  const getChangeColor = (change: number) => {
    return change > 0
      ? "bg-red-100 text-red-800"
      : change < 0
      ? "bg-green-100 text-green-800"
      : "bg-gray-100 text-gray-800"
  }

  const renderDailyRow = () => {
    return statsData.map((day: DayStats) => {
      const endStat = (day.stateCounts || []).find((s: any) => s.state === state)
      const incomingCount = (day.stateTransitions || []).reduce((total: number, t: any) => {
        if (t.to === state) {
          return total + t.count
        }
        return total
      }, 0)
      const outgoingCount = (day.stateTransitions || []).reduce((total: number, t: any) => {
        if (t.from === state) {
          return total + t.count
        }
        return total
      }, 0)
      const change = incomingCount - outgoingCount

      return (
        <React.Fragment key={day.date}>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors ${
              hasBrandData(day, state, "stateCounts") ? "text-blue-600 hover:text-blue-800" : ""
            }`}
            onClick={async () => {
              if (hasBrandData(day, state, "stateCounts")) {
                await onTotalClick(state, day)
              }
            }}
            title={
              hasBrandData(day, state, "stateCounts")
                ? "Marka detaylarını görmek için tıklayın"
                : ""
            }
          >
            {endStat ? endStat.count.toLocaleString() : "0"}
          </td>

          <td
            className={`px-0 py-4 whitespace-nowrap text-right cursor-pointer hover:bg-blue-50 transition-colors ${
              hasBrandData(day, state, "incoming") ? "text-blue-600 hover:text-blue-800" : ""
            }`}
            onClick={() => {
              if (hasBrandData(day, state, "incoming")) {
                onTransitionClick("incoming", state, day.date)
              }
            }}
            title={
              hasBrandData(day, state, "incoming") ? "Marka detaylarını görmek için tıklayın" : ""
            }
          >
            {incomingCount > 0 ? incomingCount.toLocaleString() : "0"}
          </td>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right cursor-pointer hover:bg-blue-50 transition-colors ${
              hasBrandData(day, state, "outgoing") ? "text-blue-600 hover:text-blue-800" : ""
            }`}
            onClick={() => {
              if (hasBrandData(day, state, "outgoing")) {
                onTransitionClick("outgoing", state, day.date)
              }
            }}
            title={
              hasBrandData(day, state, "outgoing") ? "Marka detaylarını görmek için tıklayın" : ""
            }
          >
            {outgoingCount > 0 ? outgoingCount.toLocaleString() : "0"}
          </td>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right font-medium ${getChangeColor(
              change,
            )}`}
          >
            {change > 0 ? `+${change}` : change}
          </td>
        </React.Fragment>
      )
    })
  }

  const renderWeeklyRow = () => {
    return statsData.map((week) => {
      const weekStat = week.stats.find((s: any) => s._id === state)
      const weekTransition = week.transitions.get(state) || {
        incoming: 0,
        outgoing: 0,
      }
      const weeklyChange = weekTransition.incoming - weekTransition.outgoing

      return (
        <React.Fragment key={week.weekNumber}>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors ${
              week.days.some((d: any) =>
                (d.stateCounts || []).some(
                  (s: any) => s.state === state && s.brands && s.brands.length > 0,
                ),
              )
                ? "text-blue-600 hover:text-blue-800"
                : ""
            }`}
            onClick={async () => {
              const hasBrandData = week.days.some((d: any) =>
                (d.stateCounts || []).some(
                  (s: any) => s.state === state && s.brands && s.brands.length > 0,
                ),
              )
              if (hasBrandData) {
                await onTotalClick(state, week)
              }
            }}
            title={
              week.days.some((d: any) =>
                (d.stateCounts || []).some(
                  (s: any) => s.state === state && s.brands && s.brands.length > 0,
                ),
              )
                ? "Marka detaylarını görmek için tıklayın"
                : ""
            }
          >
            {weekStat ? weekStat.count.toLocaleString() : "0"}
          </td>

          <td
            className={`px-0 py-4 whitespace-nowrap text-right cursor-pointer hover:bg-blue-50 transition-colors ${
              week.days.some((day: any) =>
                (day.stateTransitions || []).some(
                  (t: any) => t.to === state && t.brands && t.brands.length > 0,
                ),
              )
                ? "text-blue-600 hover:text-blue-800"
                : ""
            }`}
            onClick={() => {
              const hasBrandData = week.days.some((day: any) =>
                (day.stateTransitions || []).some(
                  (t: any) => t.to === state && t.brands && t.brands.length > 0,
                ),
              )
              if (hasBrandData) {
                onTransitionClick("incoming", state, week)
              }
            }}
            title={
              week.days.some((day: any) =>
                (day.stateTransitions || []).some(
                  (t: any) => t.to === state && t.brands && t.brands.length > 0,
                ),
              )
                ? "Marka detaylarını görmek için tıklayın"
                : ""
            }
          >
            {weekTransition.incoming > 0 ? weekTransition.incoming.toLocaleString() : "0"}
          </td>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right cursor-pointer hover:bg-blue-50 transition-colors ${
              week.days.some((day: any) =>
                (day.stateTransitions || []).some(
                  (t: any) => t.from === state && t.brands && t.brands.length > 0,
                ),
              )
                ? "text-blue-600 hover:text-blue-800"
                : ""
            }`}
            onClick={() => {
              const hasBrandData = week.days.some((day: any) =>
                (day.stateTransitions || []).some(
                  (t: any) => t.from === state && t.brands && t.brands.length > 0,
                ),
              )
              if (hasBrandData) {
                onTransitionClick("outgoing", state, week)
              }
            }}
            title={
              week.days.some((day: any) =>
                (day.stateTransitions || []).some(
                  (t: any) => t.from === state && t.brands && t.brands.length > 0,
                ),
              )
                ? "Marka detaylarını görmek için tıklayın"
                : ""
            }
          >
            {weekTransition.outgoing > 0 ? weekTransition.outgoing.toLocaleString() : "0"}
          </td>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right font-medium ${getChangeColor(
              weeklyChange,
            )}`}
          >
            {weeklyChange > 0 ? `+${weeklyChange}` : weeklyChange}
          </td>
        </React.Fragment>
      )
    })
  }

  const renderMonthlyRow = () => {
    return statsData.map((month) => {
      const monthStat = month.stats.find((s: any) => s._id === state)
      const monthTransition = month.transitions.get(state) || {
        incoming: 0,
        outgoing: 0,
      }
      const monthlyChange = monthTransition.incoming - monthTransition.outgoing

      return (
        <React.Fragment key={`${month.year}-${month.monthNumber}`}>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors ${
              hasBrandData(month, state, "stateCounts") ? "text-blue-600 hover:text-blue-800" : ""
            }`}
            onClick={async () => {
              if (hasBrandData(month, state, "stateCounts")) {
                await onTotalClick(state, month)
              }
            }}
            title={
              hasBrandData(month, state, "stateCounts")
                ? "Marka detaylarını görmek için tıklayın"
                : ""
            }
          >
            {monthStat ? monthStat.count.toLocaleString() : "0"}
          </td>

          <td
            className={`px-0 py-4 whitespace-nowrap text-right cursor-pointer hover:bg-blue-50 transition-colors ${
              hasBrandData(month, state, "incoming") ? "text-blue-600 hover:text-blue-800" : ""
            }`}
            onClick={() => {
              if (hasBrandData(month, state, "incoming")) {
                onTransitionClick("incoming", state, month)
              }
            }}
            title={
              hasBrandData(month, state, "incoming") ? "Marka detaylarını görmek için tıklayın" : ""
            }
          >
            {monthTransition.incoming > 0 ? monthTransition.incoming.toLocaleString() : "0"}
          </td>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right cursor-pointer hover:bg-blue-50 transition-colors ${
              hasBrandData(month, state, "outgoing") ? "text-blue-600 hover:text-blue-800" : ""
            }`}
            onClick={() => {
              if (hasBrandData(month, state, "outgoing")) {
                onTransitionClick("outgoing", state, month)
              }
            }}
            title={
              hasBrandData(month, state, "outgoing") ? "Marka detaylarını görmek için tıklayın" : ""
            }
          >
            {monthTransition.outgoing > 0 ? monthTransition.outgoing.toLocaleString() : "0"}
          </td>
          <td
            className={`px-0 py-4 whitespace-nowrap text-right font-medium ${getChangeColor(
              monthlyChange,
            )}`}
          >
            {monthlyChange > 0 ? `+${monthlyChange}` : monthlyChange}
          </td>
        </React.Fragment>
      )
    })
  }

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-1 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
        <span
          className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getStateColor(
            state,
          )}`}
        >
          {getStateDisplayName(state)}
        </span>
      </td>
      {viewMode === "daily"
        ? renderDailyRow()
        : viewMode === "weekly"
        ? renderWeeklyRow()
        : renderMonthlyRow()}
    </tr>
  )
}

export default StatsTableRow
